import { Layout } from "../../components/layout/Layout";
import { PageTransition } from "../../components/layout/PageTransition";
import { SEO } from "../../components/SEO";
import Badge from "../../components/ui/Badge";
import { CheckCircle2, AlertCircle, XCircle } from "lucide-react";
import styled from "styled-components";

const Section = styled.section`
  padding: 96px 16px;
  position: relative;
  overflow: hidden;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const BackgroundBlobs = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const Blob = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(128px);
  animation: pulse-slow 4s ease-in-out infinite;

  &.blob-1 {
    top: 80px;
    right: 25%;
    width: 500px;
    height: 500px;
    background: ${({ theme }) => `${theme.colors.light?.primary || "hsl(258, 90%, 58%)"}20`};
  }

  &.blob-2 {
    bottom: 80px;
    left: 25%;
    width: 400px;
    height: 400px;
    background: ${({ theme }) => `${theme.colors.light?.accent || "hsl(16, 90%, 58%)"}20`};
    animation-delay: 2s;
  }
`;

const Container = styled.div`
  max-width: 896px;
  margin: 0 auto;
  position: relative;
  z-index: 10;
`;

const HeroContent = styled.div`
  max-width: 768px;
  margin: 0 auto;
  text-align: center;
  margin-bottom: 64px;
`;

const BadgeWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: ${({ theme }) => theme.typography.fontSize.h1};
  font-weight: 700;
  line-height: 1.1;
  margin: 0 0 24px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const GradientText = styled.span`
  background: ${({ theme }) => theme.colors.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.bodyLarge};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const StatusList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StatusItem = styled.div`
  padding: 24px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.light?.card?.background || theme.colors.light?.background || "white"};
  border: 1px solid ${({ theme }) => theme.colors.light?.border || "hsl(240, 10%, 90%)"};
  display: flex;
  align-items: center;
  gap: 16px;

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.card?.background || theme.colors.dark?.background || "hsl(240, 15%, 10%)"};
    border-color: ${({ theme }) => theme.colors.dark?.border || "hsl(240, 15%, 18%)"};
  }
`;

const StatusIcon = styled.div`
  flex-shrink: 0;
`;

const StatusContent = styled.div`
  flex: 1;
`;

const StatusName = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-weight: 600;
  margin: 0 0 4px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const StatusMessage = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};
  margin: 0;

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const services = [
  { name: "API", status: "operational", message: "All systems operational" },
  { name: "Dashboard", status: "operational", message: "All systems operational" },
  { name: "Webhooks", status: "operational", message: "All systems operational" },
  { name: "Authentication", status: "operational", message: "All systems operational" },
];

export default function Status() {
  return (
    <Layout>
      <SEO
        title="Status"
        description="Check the current status of Kliq CRM services and systems."
        keywords="status, uptime, system status, monitoring"
        hreflang
      />
      <PageTransition>
        <Section>
          <BackgroundBlobs>
            <Blob className="blob-1" />
            <Blob className="blob-2" />
          </BackgroundBlobs>
          <Container>
            <HeroContent>
              <BadgeWrapper>
                <Badge $variant="primary">Status</Badge>
              </BadgeWrapper>
              <Title>
                System <GradientText>Status</GradientText>
              </Title>
              <Description>
                Check the current status of Kliq CRM services and systems.
              </Description>
            </HeroContent>

            <StatusList>
              {services.map((service) => (
                <StatusItem key={service.name}>
                  <StatusIcon>
                    <CheckCircle2 size={24} style={{ color: "hsl(142, 76%, 36%)" }} />
                  </StatusIcon>
                  <StatusContent>
                    <StatusName>{service.name}</StatusName>
                    <StatusMessage>{service.message}</StatusMessage>
                  </StatusContent>
                </StatusItem>
              ))}
            </StatusList>
          </Container>
        </Section>
      </PageTransition>
    </Layout>
  );
}
